import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { db } from '../FirebaseConfig';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { ChevronLeft, Award, MapPin, Package } from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import noImg from "../assets/ChatGPT Image May 4, 2025, 05_02_10 AM.png";

// ────────────────────── STORE PAGE ──────────────────────

const StorePage = () => {
    const { sellerId } = useParams();
    const location = useLocation();
    const canvasRef = useRef(null);
    
    const [storeInfo, setStoreInfo] = useState(null);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isVerified, setIsVerified] = useState(false);

    const storeName = location.state?.storeName || 'Store';

    useEffect(() => {
        const fetchStoreData = async () => {
            setLoading(true);
            try {
                // Fetch seller profile
                const sellerRef = doc(db, 'profiles', sellerId);
                const sellerSnap = await getDoc(sellerRef);

                if (sellerSnap.exists()) {
                    const data = sellerSnap.data();
                    setStoreInfo({
                        name: data.storeName || storeName,
                        university: data.university,
                        verified: data.verification_status === 'approved',
                    });
                    setIsVerified(data.verification_status === 'approved');
                }

                // Fetch all products by this seller
                const productsRef = collection(db, 'Products');
                const q = query(
                    productsRef,
                    where('sellerId', '==', sellerId),
                    where('approved', '==', true)
                );

                const snapshot = await getDocs(q);
                const productList = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                }));

                setProducts(productList);
            } catch (err) {
                console.error('Error fetching store data:', err);
                toast.error('Failed to load store');
            }
            setLoading(false);
        };

        if (sellerId) {
            fetchStoreData();
        }
    }, [sellerId, storeName]);

    return (
        <div className="relative min-h-screen text-white overflow-hidden">
            {/* Canvas Background */}
            <canvas ref={canvasRef} className="fixed top-0 left-0 w-full h-full z-0 opacity-70" />

            {/* Content */}
            <div className="relative z-10">
                {/* Back Button & Header */}
                <div className="sticky top-0 z-20 bg-black/50 backdrop-blur-md border-b border-white/10">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
                        <Link
                            to="/product"
                            className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors mb-4 w-fit"
                        >
                            <ChevronLeft size={20} />
                            <span className="font-semibold">Back to Browse</span>
                        </Link>

                        {loading ? (
                            <div className="h-16 bg-white/5 rounded-xl animate-pulse" />
                        ) : (
                            <div>
                                <h1 className="text-4xl font-bold text-white mb-2">
                                    {storeInfo?.name || storeName}
                                </h1>
                                <div className="flex flex-wrap items-center gap-4 text-sm">
                                    {isVerified && (
                                        <div className="flex items-center gap-2 bg-emerald-500/20 border border-emerald-500/30 rounded-full px-3 py-1">
                                            <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                                            <span className="text-emerald-300 font-semibold">Verified Seller</span>
                                        </div>
                                    )}
                                    {storeInfo?.university && (
                                        <div className="flex items-center gap-2 text-gray-300">
                                            <MapPin size={16} className="text-gray-400" />
                                            <span>{storeInfo.university}</span>
                                        </div>
                                    )}
                                    <div className="flex items-center gap-2 text-gray-300">
                                        <Package size={16} className="text-gray-400" />
                                        <span>{products.length} products</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Main Content */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
                    {loading ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                            {[...Array(12)].map((_, i) => (
                                <div key={i} className="bg-white/5 rounded-xl h-64 animate-pulse" />
                            ))}
                        </div>
                    ) : products.length > 0 ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                            {products.map((product) => (
                                <motion.div
                                    key={product.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    whileHover={{ y: -8 }}
                                    transition={{ duration: 0.3 }}
                                    className="group"
                                >
                                    <Link to={`/product/${product.id}`} className="block h-full">
                                        <div className="h-full bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-md rounded-2xl overflow-hidden border border-white/10 hover:border-emerald-500/50 transition-all duration-300 shadow-lg hover:shadow-2xl hover:shadow-emerald-500/10 flex flex-col">
                                            {/* Image */}
                                            <div className="relative w-full aspect-square overflow-hidden bg-gradient-to-br from-slate-800 to-slate-900">
                                                <img
                                                    src={product.imageUrl || noImg}
                                                    loading="lazy"
                                                    alt={product.name}
                                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                                />
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />

                                                {/* Condition badge */}
                                                {product.condition && (
                                                    <div className={`absolute top-3 left-3 text-xs font-bold px-3 py-1 rounded-full shadow-lg ${
                                                        product.condition === 'New'
                                                            ? 'bg-emerald-500/90 text-white'
                                                            : 'bg-amber-500/90 text-white'
                                                    }`}>
                                                        {product.condition === 'New' ? '✨ New' : '♻️ Used'}
                                                    </div>
                                                )}

                                                {/* Price Badge */}
                                                <div className="absolute bottom-3 right-3 bg-gradient-to-r from-emerald-500 to-blue-500 text-white font-bold text-sm px-4 py-2 rounded-full shadow-lg">
                                                    ₦{Number(product.price).toLocaleString()}
                                                </div>
                                            </div>

                                            {/* Content */}
                                            <div className="p-3 flex flex-col flex-grow">
                                                <h3 className="text-sm font-bold text-white mb-1 line-clamp-2 group-hover:text-emerald-300 transition-colors">
                                                    {product.name}
                                                </h3>

                                                <p className="text-xs text-gray-400 mb-2 line-clamp-2 flex-grow">
                                                    {product.description}
                                                </p>

                                                <motion.button
                                                    whileHover={{ scale: 1.02 }}
                                                    whileTap={{ scale: 0.98 }}
                                                    className="w-full bg-gradient-to-r from-gray-900 to-emerald-800 hover:from-black hover:to-emerald-900 text-white font-bold py-2 rounded-xl transition-all duration-200 text-xs shadow-lg shadow-black/30"
                                                >
                                                    View Details
                                                </motion.button>
                                            </div>
                                        </div>
                                    </Link>
                                </motion.div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-20">
                            <p className="text-6xl mb-4">🏪</p>
                            <p className="text-xl font-bold text-white mb-2">No Products Yet</p>
                            <p className="text-gray-400 mb-6">This store hasn't listed any products yet</p>
                            <Link
                                to="/"
                                className="inline-block bg-gradient-to-r from-emerald-500 to-blue-500 text-white font-bold px-6 py-3 rounded-xl hover:from-emerald-600 hover:to-blue-600 transition-all"
                            >
                                Continue Shopping
                            </Link>
                        </div>
                    )}
                </div>

                {/* Footer spacing */}
                <div className="h-20" />
            </div>

            <ToastContainer />
        </div>
    );
};

export default StorePage;